import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output } from '@angular/core';
import { MatRadioModule } from '@angular/material/radio';
import { CircularProgressComponent } from '../../../../components/circular-progress/circular-progress.component';
import { EnfafButtonComponent } from '../../../../components/enfaf-button/enfaf-button.component';
import { EnfafIconComponent } from '../../../../components/enfaf-icon/enfaf-icon.component';
import { QuizQuestionStudent } from '../../../../models/interfaces/quiz.interface';

@Component({
  selector: 'app-quiz-question',
  standalone: true,
  imports: [MatRadioModule, CircularProgressComponent, EnfafButtonComponent, EnfafIconComponent],
  templateUrl: './quiz-question.component.html',
  styleUrl: './quiz-question.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuizQuestionComponent {
  question = input.required<QuizQuestionStudent>();
  /** Previously saved answer for this question, passed down from the parent. */
  selectedOption = input<number | null>(null);
  questionNumber = input.required<number>();
  totalQuestions = input(2);
  isSubmitting = input(false);

  answered = output<{ quizQuestionId: string; selectedOptionIndex: number }>();
  back = output<void>();
  close = output<void>();

  /**
   * The pending selection for the currently displayed question.
   * linkedSignal resets to selectedOption() whenever `question` or `selectedOption` changes,
   * so stale state from a previous question never carries over.
   */
  pendingSelection = linkedSignal<number | null>(() => {
    this.question(); // track question identity to force reset on question change
    return this.selectedOption();
  });

  progressPercent = computed(() => {
    const count = this.pendingSelection() !== null ? this.questionNumber() : this.questionNumber() - 1;
    return Math.round((count / this.totalQuestions()) * 100);
  });

  answeredCount = computed(() => (this.pendingSelection() !== null ? this.questionNumber() : this.questionNumber() - 1));

  onSelectOption(index: number): void {
    this.pendingSelection.set(index);
  }

  optionLabel(index: number): string {
    return String.fromCharCode(65 + index);
  }

  onSubmit(): void {
    const option = this.pendingSelection();
    if (option === null) return;
    this.answered.emit({
      quizQuestionId: this.question().quizQuestionId,
      selectedOptionIndex: option
    });
  }

  onGoBack(): void {
    this.back.emit();
  }
}
